import { UseQueryOptions } from '@tanstack/react-query';

import { Campaign } from 'domains/campaigns/types';
import useQuery from 'hooks/useQuery';
import { CampaignStatus, CuredApiPaths, SortDirection } from 'models/enums';

import QueryKeys from './keys';

export type PaginatedCampaignListResponse = {
    total: number;
    offset: number;
    limit: number;
    data: Campaign[];
};

type UsePaginatedCampaignsParams = {
    options?: {
        searchTerm?: string;
        statuses?: CampaignStatus[];
        limit?: number;
        offset?: number;
        sortColumn?: string;
        sortDirection?: SortDirection;
    };
    reactQueryOptions?: UseQueryOptions<PaginatedCampaignListResponse, Error>;
};

const usePaginatedCampaigns = ({ options = {}, reactQueryOptions }: UsePaginatedCampaignsParams = {}) => {
    const { searchTerm, statuses, limit, offset, sortColumn, sortDirection } = options;

    const queryKey = QueryKeys.CAMPAIGNS_PAGINATED(
        searchTerm,
        statuses?.join(','),
        offset,
        limit,
        sortColumn,
        sortDirection
    );

    const params: Record<string, string | number> = {};
    if (searchTerm?.trim()) {
        params.searchTerm = searchTerm.trim();
    }
    if (statuses && statuses.length > 0) {
        params.status = statuses.join(',');
    }
    if (limit !== undefined) {
        params.limit = limit;
    }
    if (offset !== undefined) {
        params.offset = offset;
    }
    if (sortColumn && sortDirection) {
        params.sort = `${sortColumn}:${sortDirection}`;
    }

    const axiosOptions = Object.keys(params).length > 0 ? { params } : {};

    return useQuery<PaginatedCampaignListResponse>(
        queryKey,
        CuredApiPaths.CAMPAIGNS_PAGINATED,
        reactQueryOptions,
        axiosOptions
    );
};

export default usePaginatedCampaigns;
